/**
 * Star (Wi-Fi) printers surface two superficially similar but semantically OPPOSITE
 * connection conditions. They must be classified separately, otherwise a successful
 * print is reported as a failure (TSP143IIIW):
 *
 *  1. POST-WRITE DISCONNECT — "Printer disconnected", surfaced by the plugin's
 *     checkIsPrinterDisconnected() AFTER a successful writePort. On Wi-Fi Star
 *     models (notably TSP143IIIW) the printer routinely drops the TCP connection
 *     once it has accepted and printed the data. The physical print SUCCEEDED;
 *     this is normal model behaviour, not an error → treat as success.
 *
 *  2. PORT-OPEN FAILURE — "Failed to open a LAN port", surfaced BEFORE any data is
 *     written, typically because the previous socket is still in TIME_WAIT on the
 *     printer side. Nothing was printed → the connection is safe to retry (no
 *     duplicate risk for the part that failed to connect).
 *
 * Keeping these as the single source of truth so policy tweaks do not leak into the
 * printer service.
 *
 * Since Star plugin 7.3.2 the plugin no longer emits "Printer disconnected": it sends tokenized
 * messages (see star-print-outcome.ts) that separate a printer-confirmed "not ready" from a
 * connection problem and from a post-write uncertainty. The detectors below keep the exact
 * legacy semantics for older plugin versions, so a plugin rollback behaves as before.
 */

function messageOf(error: unknown): string {
  return typeof error === 'object' && error !== null && 'message' in error
    ? String((error as { message?: unknown }).message ?? '')
    : String(error ?? '');
}

/** Post-write disconnect on Wi-Fi Star: the data was written and printed before the drop. */
const STAR_POST_WRITE_DISCONNECT_PATTERNS: ReadonlyArray<RegExp> = [/printer disconnected/i];

/** Pre-write connection failure (often the previous socket still in TIME_WAIT). */
const STAR_PORT_OPEN_FAILURE_PATTERNS: ReadonlyArray<RegExp> = [/failed to open a lan port/i];

/**
 * True when the error represents a post-write TCP drop, i.e. the print most likely
 * completed and the result should be treated as SUCCESS rather than a failure.
 */
export function isStarPostWriteDisconnect(error: unknown): boolean {
  const message = messageOf(error);
  if (!message) return false;
  return STAR_POST_WRITE_DISCONNECT_PATTERNS.some((pattern) => pattern.test(message));
}

const STAR_LEGACY_POST_WRITE_STATUS_PATTERN = /StarIOPortException: (Printer cover is open|Receipt paper is empty)$/;

export function isStarLegacyPostWriteStatus(error: unknown): boolean {
  return STAR_LEGACY_POST_WRITE_STATUS_PATTERN.test(messageOf(error).trim());
}

/** Physical reason of a legacy post-write status text (COVER_OPEN / PAPER_EMPTY), if it is one. */
export function starLegacyPostWriteReason(error: unknown): 'COVER_OPEN' | 'PAPER_EMPTY' | undefined {
  const match = STAR_LEGACY_POST_WRITE_STATUS_PATTERN.exec(messageOf(error).trim());
  if (!match) {
    return undefined;
  }
  return match[1] === 'Printer cover is open' ? 'COVER_OPEN' : 'PAPER_EMPTY';
}

/**
 * True when the error represents a pre-write "could not open the LAN port" condition.
 * Nothing was written, so the connection attempt for that job is safe to retry.
 */
export function isStarPortOpenFailure(error: unknown): boolean {
  const message = messageOf(error);
  if (!message) return false;
  return STAR_PORT_OPEN_FAILURE_PATTERNS.some((pattern) => pattern.test(message));
}
